import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { AppShell } from "../components/AppShell";
import { SchemaPrint } from "../components/SchemaPrint";
import { Badge } from "../components/Badge";
import { SCHEMAS, permitMeta } from "../lib/permitSchemas";
import logo from "../assets/gfm-logo.png";
import { Button } from "../components/ui/button";
import { ArrowLeft, Printer, IdCard, Loader2, XCircle, Ban, Upload, FileText, Trash2, ClipboardCheck } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { SignaturePad } from "../components/SignaturePad";
import { toast } from "sonner";
import { formatApiErrorDetail } from "../lib/api";

function fmt(dt) {
  if (!dt) return "—";
  try {
    return new Date(dt).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return dt;
  }
}

export default function PermitDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [permit, setPermit] = useState(null);
  const [mode, setMode] = useState("permits"); // controls print scope
  const [cancelOpen, setCancelOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [cancelling, setCancelling] = useState(false);
  const [uploadingReport, setUploadingReport] = useState(false);
  const reportRef = useRef(null);
  const [clearOpen, setClearOpen] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [clr, setClr] = useState({ area_clean: false, pic_name: "", pic_signature: null, pic_date: "", gfm_name: "", gfm_signature: null, gfm_datetime: "" });

  const reload = () => api.get(`/permits/${id}`).then((r) => setPermit(r.data));

  const onUploadReport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingReport(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/permits/${id}/reports`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Report uploaded");
      await reload();
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally {
      setUploadingReport(false);
      if (reportRef.current) reportRef.current.value = "";
    }
  };

  const deleteReport = async (rid) => {
    await api.delete(`/permits/${id}/reports/${rid}`);
    toast.success("Report removed");
    reload();
  };

  const openClearance = () => {
    const now = new Date(); now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
    setClr({
      area_clean: permit.clearance?.area_clean || false,
      pic_name: permit.clearance?.pic_name || permit.pic_name || "",
      pic_signature: permit.clearance?.pic_signature || null,
      pic_date: permit.clearance?.pic_date || now.toISOString().slice(0, 10),
      gfm_name: permit.clearance?.gfm_name || permit.issuer_name || "",
      gfm_signature: permit.clearance?.gfm_signature || null,
      gfm_datetime: permit.clearance?.gfm_datetime || now.toISOString().slice(0, 16),
    });
    setClearOpen(true);
  };
  const saveClearance = async () => {
    if (!clr.area_clean) return toast.error("Please confirm the area was left clean and tidy.");
    setClearing(true);
    try {
      const { data } = await api.post(`/permits/${id}/clearance`, clr);
      setPermit(data);
      setClearOpen(false);
      toast.success("Sign-off saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setClearing(false);
    }
  };

  useEffect(() => {
    api.get(`/permits/${id}`).then((r) => setPermit(r.data)).catch(() => navigate("/"));
  }, [id]);

  const doPrint = (m) => {
    setMode(m);
    setTimeout(() => window.print(), 100);
  };

  const doCancel = async () => {
    setCancelling(true);
    try {
      const { data } = await api.post(`/permits/${id}/cancel`, { reason });
      setPermit(data);
      setCancelOpen(false);
      toast.success("Permit cancelled");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setCancelling(false);
    }
  };

  if (!permit)
    return (
      <AppShell>
        <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>
      </AppShell>
    );

  return (
    <>
      {/* Screen UI */}
      <div className="print-hidden">
        <AppShell>
          <button onClick={() => navigate("/")} className="mb-4 flex items-center gap-1 text-sm text-muted-foreground hover:text-brand" data-testid="back-btn">
            <ArrowLeft className="h-4 w-4" /> Back to dashboard
          </button>
          <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
            <div>
              <div className="text-xs font-semibold uppercase tracking-[0.2em] text-brand">{permit.permit_number}</div>
              <h1 className="font-heading text-3xl font-bold tracking-tight">{permit.contractor_name}</h1>
              <p className="text-muted-foreground">{permit.company} · {permit.site_location}</p>
              {permit.cancelled_at && (
                <span className="mt-2 inline-flex items-center gap-1 rounded-full bg-red-100 px-3 py-1 text-xs font-semibold text-red-700" data-testid="cancelled-badge">
                  <Ban className="h-3.5 w-3.5" /> Cancelled {fmt(permit.cancelled_at)}
                </span>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => doPrint("badge")} data-testid="print-badge-btn">
                <IdCard className="mr-2 h-4 w-4" /> Print ID badge
              </Button>
              <Button className="bg-brand hover:bg-brand-dark" onClick={() => doPrint("permits")} data-testid="print-permits-btn">
                <Printer className="mr-2 h-4 w-4" /> Print A4 permits
              </Button>
              {!permit.cancelled_at && (
                <Button variant="outline" className="border-destructive/40 text-destructive hover:bg-destructive/5" onClick={() => setCancelOpen(true)} data-testid="cancel-permit-btn">
                  <XCircle className="mr-2 h-4 w-4" /> Cancel permit
                </Button>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
            <div className="lg:col-span-4">
              <div className="sticky top-24">
                <Badge permit={permit} />
                <div className="mt-4 rounded-md border border-border bg-white p-4 text-sm shadow-sm">
                  <Row label="Issued" value={fmt(permit.issue_at)} />
                  <Row label="Expires" value={fmt(permit.expiry_at)} />
                  {permit.contractor_mobile && <Row label="Mobile" value={permit.contractor_mobile} />}
                  <Row label="Authorised by" value={permit.issuer_name} />
                  <Row label="Person in Charge" value={permit.pic_name || "—"} />
                  <Row label="Issued by" value={permit.created_by} />
                  {permit.cancelled_at && <Row label="Cancelled by" value={permit.cancelled_by} />}
                  {permit.cancelled_at && permit.cancel_reason && <Row label="Reason" value={permit.cancel_reason} />}
                </div>
              </div>
            </div>
            <div className="space-y-4 lg:col-span-8">
              {(permit.permit_types || []).map((t) => (
                <div key={t} className="rounded-md border border-border bg-white p-6 shadow-sm" data-testid={`detail-${t}`}>
                  <div className="mb-4 flex items-center gap-2">
                    <span className="inline-block h-3 w-3 rounded-full" style={{ background: permitMeta(t)?.color }} />
                    <h3 className="font-heading text-lg font-bold tracking-tight">{SCHEMAS[t].title}</h3>
                  </div>
                  <SchemaPrint schema={SCHEMAS[t]} data={permit.permit_data?.[t] || {}} />
                </div>
              ))}

              {/* Completion sign-off */}
              <div className="rounded-md border border-border bg-white p-6 shadow-sm" data-testid="clearance-section">
                <div className="mb-4 flex items-center justify-between">
                  <h3 className="font-heading text-lg font-bold tracking-tight">Completion sign-off</h3>
                  <Button size="sm" variant="outline" onClick={openClearance} data-testid="clearance-btn">
                    <ClipboardCheck className="mr-2 h-4 w-4" /> {permit.clearance ? "Update sign-off" : "Sign off"}
                  </Button>
                </div>
                {permit.clearance ? (
                  <div className="space-y-2 text-sm" data-testid="clearance-details">
                    <div className="flex items-center gap-2 font-medium text-green-700">
                      <ClipboardCheck className="h-4 w-4" /> Area(s) left clean and tidy
                    </div>
                    <div className="grid grid-cols-2 gap-4 pt-2">
                      <div>
                        <div className="text-xs uppercase tracking-wide text-muted-foreground">Person in Charge</div>
                        <div className="font-medium">{permit.clearance.pic_name || "—"}</div>
                        {permit.clearance.pic_signature && <img src={permit.clearance.pic_signature} alt="sig" className="mt-1 h-12 object-contain" />}
                        <div className="text-xs text-muted-foreground">Date: {permit.clearance.pic_date || "—"}</div>
                      </div>
                      <div>
                        <div className="text-xs uppercase tracking-wide text-muted-foreground">GFM Appointed Person</div>
                        <div className="font-medium">{permit.clearance.gfm_name || "—"}</div>
                        {permit.clearance.gfm_signature && <img src={permit.clearance.gfm_signature} alt="sig" className="mt-1 h-12 object-contain" />}
                        <div className="text-xs text-muted-foreground">Date/time: {fmt(permit.clearance.gfm_datetime)}</div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground" data-testid="clearance-empty">Not signed off yet. When the contractor leaves site, record that the area was left clean and tidy — the Person in Charge and the GFM Appointed Person both sign.</p>
                )}
              </div>

              {/* Contractor reports */}
              <div className="rounded-md border border-border bg-white p-6 shadow-sm" data-testid="reports-section">
                <div className="mb-4 flex items-center justify-between">
                  <h3 className="font-heading text-lg font-bold tracking-tight">Contractor reports</h3>
                  <input ref={reportRef} type="file" className="hidden" onChange={onUploadReport} data-testid="report-file-input" accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.webp,.csv,.txt" />
                  <Button size="sm" variant="outline" onClick={() => reportRef.current?.click()} disabled={uploadingReport} data-testid="report-upload-btn">
                    {uploadingReport ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />} Upload
                  </Button>
                </div>
                <p className="mb-3 text-sm text-muted-foreground">Upload completion reports, inspection sheets or photos related to this permit.</p>
                {(!permit.reports || permit.reports.filter((r) => !r.is_deleted).length === 0) ? (
                  <div className="rounded-md border border-dashed border-border py-8 text-center text-sm text-muted-foreground" data-testid="reports-empty">No reports uploaded yet.</div>
                ) : (
                  <ul className="divide-y divide-border rounded-md border border-border">
                    {permit.reports.filter((r) => !r.is_deleted).map((r) => (
                      <li key={r.id} className="flex items-center justify-between gap-3 px-4 py-3" data-testid={`report-item-${r.id}`}>
                        <a href={`${process.env.REACT_APP_BACKEND_URL}/api/permits/${permit.id}/reports/${r.id}/download`} target="_blank" rel="noreferrer" className="flex min-w-0 items-center gap-2 text-sm font-medium text-brand hover:underline">
                          <FileText className="h-4 w-4 flex-shrink-0" /><span className="truncate">{r.original_filename}</span>
                        </a>
                        <div className="flex items-center gap-3">
                          <span className="whitespace-nowrap text-xs text-muted-foreground">{(r.size / 1024).toFixed(0)} KB</span>
                          <button onClick={() => deleteReport(r.id)} className="text-muted-foreground hover:text-destructive" data-testid={`report-delete-${r.id}`}><Trash2 className="h-4 w-4" /></button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        </AppShell>
      </div>

      {/* Cancel dialog */}
      <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <DialogContent className="max-w-md" data-testid="cancel-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Cancel permit before expiry</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Use this when the contractor has left site. The permit will be marked cancelled and no longer counts as active.
          </p>
          <div className="space-y-1.5 py-2">
            <label className="text-sm font-medium">Reason (optional)</label>
            <Textarea data-testid="cancel-reason" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Works complete, contractor left site" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelOpen(false)}>Keep permit</Button>
            <Button className="bg-destructive text-white hover:bg-destructive/90" onClick={doCancel} disabled={cancelling} data-testid="confirm-cancel-btn">
              {cancelling && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Cancel permit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Clearance / sign-off dialog */}
      <Dialog open={clearOpen} onOpenChange={setClearOpen}>
        <DialogContent className="max-w-lg" data-testid="clearance-dialog">
          <DialogHeader><DialogTitle className="font-heading">Completion sign-off</DialogTitle></DialogHeader>
          <label className="flex cursor-pointer items-start gap-3 rounded-md border border-border px-3 py-2.5">
            <Checkbox checked={clr.area_clean} onCheckedChange={(c) => setClr((s) => ({ ...s, area_clean: !!c }))} data-testid="clearance-area-clean" className="mt-0.5" />
            <span className="text-sm">The work area(s) have been left clean and tidy and all waste removed.</span>
          </label>
          <div className="grid grid-cols-1 gap-4 py-2 sm:grid-cols-2">
            <div className="space-y-2">
              <div className="text-xs font-semibold uppercase tracking-wide text-brand">Person in Charge</div>
              <Input placeholder="Name" data-testid="clearance-pic-name" value={clr.pic_name} onChange={(e) => setClr((s) => ({ ...s, pic_name: e.target.value }))} />
              <div className="space-y-1"><Label className="text-xs">Date</Label><Input type="date" data-testid="clearance-pic-date" value={clr.pic_date} onChange={(e) => setClr((s) => ({ ...s, pic_date: e.target.value }))} /></div>
              <SignaturePad label="Signature" testid="clearance-pic-sig" value={clr.pic_signature} onChange={(v) => setClr((s) => ({ ...s, pic_signature: v }))} />
            </div>
            <div className="space-y-2">
              <div className="text-xs font-semibold uppercase tracking-wide text-brand">GFM Appointed Person</div>
              <Input placeholder="Name" data-testid="clearance-gfm-name" value={clr.gfm_name} onChange={(e) => setClr((s) => ({ ...s, gfm_name: e.target.value }))} />
              <div className="space-y-1"><Label className="text-xs">Date & time</Label><Input type="datetime-local" data-testid="clearance-gfm-datetime" value={clr.gfm_datetime} onChange={(e) => setClr((s) => ({ ...s, gfm_datetime: e.target.value }))} /></div>
              <SignaturePad label="Signature" testid="clearance-gfm-sig" value={clr.gfm_signature} onChange={(v) => setClr((s) => ({ ...s, gfm_signature: v }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setClearOpen(false)}>Cancel</Button>
            <Button className="bg-brand hover:bg-brand-dark" onClick={saveClearance} disabled={clearing} data-testid="save-clearance-btn">{clearing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save sign-off</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Print area */}
      <div className="print-only">
        {mode === "badge" ? (
          <div className="p-8"><Badge permit={permit} /></div>
        ) : (
          (permit.permit_types || []).map((t, i) => (
            <div key={t} className={`a4-sheet permit-print relative bg-white ${i > 0 ? "page-break" : ""}`}>
              {permit.cancelled_at && (
                <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                  <span className="rotate-[-30deg] rounded border-4 border-red-500 px-8 py-2 text-5xl font-extrabold uppercase tracking-widest text-red-500 opacity-30">Cancelled</span>
                </div>
              )}
              <PrintHeader permit={permit} schema={SCHEMAS[t]} />
              <div className="permit-cols">
                <SchemaPrint schema={SCHEMAS[t]} data={permit.permit_data?.[t] || {}} />
              </div>
              <Signatures permit={permit} />
            </div>
          ))
        )}
      </div>
    </>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between border-b border-border py-1.5 last:border-0">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium">{value}</span>
    </div>
  );
}

function PrintHeader({ permit, schema }) {
  return (
    <div className="mb-4 border-b-2 border-brand pb-3">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] leading-tight text-neutral-600">
            Level 3 : Quality Operations Procedures<br />
            Type : GFM Compliance Manual — Section 41<br />
            Name : {schema.title} · Sponsor : Chief Engineer
          </div>
        </div>
        <img src={logo} alt="GFM" className="h-9" />
      </div>
      <div className="mt-3 flex items-end justify-between">
        <h1 className="font-heading text-2xl font-extrabold uppercase tracking-tight text-brand">{schema.title}</h1>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-wider text-neutral-500">Serial Number</div>
          <div className="font-heading text-xl font-bold text-brand">{permit.permit_number}</div>
        </div>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-x-6 gap-y-1 text-[12px]">
        <div><b>Name:</b> {permit.contractor_name}</div>
        <div><b>Company:</b> {permit.company}</div>
        <div><b>ID No:</b> {permit.id_number}</div>
        <div><b>Mobile:</b> {permit.contractor_mobile || "—"}</div>
        <div><b>Site:</b> {permit.site_location}</div>
        <div><b>Valid from:</b> {fmt(permit.issue_at)}</div>
        <div><b>Expiry:</b> {fmt(permit.expiry_at)}</div>
      </div>
    </div>
  );
}

function Signatures({ permit }) {
  const cl = permit.clearance;
  return (
    <div className="no-break mt-6 border-t-2 border-brand pt-4">
      <div className="grid grid-cols-2 gap-6">
        <SigBlock title="Authorised Person" name={permit.issuer_name} sub={permit.issuer_job_title} sig={permit.issuer_signature} date={permit.issue_at} />
        <SigBlock title="Person in Charge" name={permit.pic_name} sig={permit.pic_signature} date={permit.issue_at} />
      </div>
      <div className="mt-4 rounded border border-neutral-400 p-2">
        <div className="text-[11px] font-bold uppercase tracking-wider text-brand">Completion sign-off</div>
        <div className="mb-2 text-[11px]">
          <span className="mr-1 inline-flex h-3.5 w-3.5 items-center justify-center border border-neutral-500 text-[9px] font-bold align-middle">{cl?.area_clean ? "\u2713" : ""}</span>
          The work area(s) have been left clean and tidy and all waste removed.
        </div>
        <div className="grid grid-cols-2 gap-6">
          <SigBlock title="Person in Charge" name={cl?.pic_name || permit.pic_name} sig={cl?.pic_signature} date={cl?.pic_date} />
          <SigBlock title="GFM Appointed Person" name={cl?.gfm_name} sig={cl?.gfm_signature} date={cl?.gfm_datetime} />
        </div>
      </div>
      <div className="mt-3 text-center text-[10px] italic text-neutral-600">
        This permit is not valid unless Sections 1 &amp; 2 have been signed by the Authorised Person and Person in Charge.
      </div>
    </div>
  );
}

function SigBlock({ title, name, sub, sig, date }) {
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-wider text-brand">{title}</div>
      <div className="mt-1 h-14 border-b border-neutral-400">
        {sig && <img src={sig} alt="signature" className="h-14 object-contain" />}
      </div>
      <div className="mt-1 text-[12px] font-semibold">{name || "—"}</div>
      {sub && <div className="text-[10px] text-neutral-500">{sub}</div>}
      <div className="text-[10px] text-neutral-500">Date: {fmt(date)}</div>
    </div>
  );
}
